import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, addDoc, query, where, doc, setDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import HRCampusSelector from "../../components/HRCampusSelector";
import { getCurrentUser } from "../../auth";

export default function AttendanceDaily() {
  const [campus, setCampus] = useState(() => localStorage.getItem("hr_att_campus") || "bulacan");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState({});
  const [inTime, setInTime] = useState({});
  const [outTime, setOutTime] = useState({});
  const [lateMin, setLateMin] = useState({});
  const [otMin, setOtMin] = useState({});
  const [note, setNote] = useState({});
  const [shiftStart, setShiftStart] = useState(() => localStorage.getItem("hr_att_shift_start") || "07:00");
  const [shiftEnd, setShiftEnd] = useState(() => localStorage.getItem("hr_att_shift_end") || "16:00");
  const [loading, setLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [mapText, setMapText] = useState(() => localStorage.getItem("hr_att_bio_map_text") || "");
  const [showMap, setShowMap] = useState(false);

  function empLsKey(c) {
    return `employees_${String(c || "gen").toLowerCase()}`;
  }
  function loadLocalEmployeesLS(c) {
    try {
      const raw = localStorage.getItem(empLsKey(c));
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }

  function normalizeCampusLabel(x) {
    const s = String(x || "").toLowerCase();
    if (s.includes("gapan")) return "gapan";
    if (s.includes("bulacan")) return "bulacan";
    return s;
  }
  const currentUser = getCurrentUser();
  const roleStr = String(currentUser?.role || "").toLowerCase();
  const rolesArr = Array.isArray(currentUser?.roles) ? currentUser.roles.map(r => String(r).toLowerCase()) : [];
  const hasRole = (r) => roleStr === r || rolesArr.includes(r);
  const isExec = hasRole("admin") || hasRole("superadmin") || hasRole("vicepresident") || hasRole("vp") || hasRole("board") || hasRole("bot") || String(currentUser?.campus || "").toLowerCase() === "all";
  useEffect(() => {
    localStorage.setItem("hr_att_campus", campus);
    try { localStorage.setItem("hr_att_shift_start", shiftStart); } catch(e) { void e; }
    try { localStorage.setItem("hr_att_shift_end", shiftEnd); } catch(e) { void e; }
    (async () => {
      let emps = [];
      try {
        const snap = await getDocs(collection(db, "employees"));
        if (!isExec || campus) {
          const sc = normalizeCampusLabel(campus);
          emps = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => normalizeCampusLabel(e.campus) === sc);
        } else {
          emps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        }
      } catch (e) { emps = loadLocalEmployeesLS(campus); }
      emps.sort((a,b)=>String(a.name||"").localeCompare(String(b.name||"")));
      setEmployees(emps);
      // Load existing for the date to prefill
      try {
        const aSnap = await getDocs(collection(db, "employees_attendance"));
        let list = aSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(a => String(a.date||"")===date);
        if (!isExec || campus) {
          const sc = normalizeCampusLabel(campus);
          list = list.filter(a => normalizeCampusLabel(a.campus) === sc);
        }
        const s={}, iT={}, oT={}, lm={}, om={}, nt={};
        for (const r of list) {
          const key = r.employeeName;
          s[key] = r.status || s[key] || "";
          iT[key] = r.inTime || "";
          oT[key] = r.outTime || "";
          lm[key] = r.minutesLate || "";
          om[key] = r.overtimeMinutes || "";
          nt[key] = r.notes || "";
        }
        setStatus(s); setInTime(iT); setOutTime(oT); setLateMin(lm); setOtMin(om); setNote(nt);
      } catch (e) { void e; }
    })();
  }, [campus, date, isExec, shiftStart, shiftEnd]);

  function toMinutes(t) {
    if (!t || typeof t !== "string") return NaN;
    const m = t.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return NaN;
    return Number(m[1]) * 60 + Number(m[2]);
    }
  function computeFor(name, e) {
    const st = e?.shiftStart || shiftStart;
    const et = e?.shiftEnd || shiftEnd;
    const sin = toMinutes(inTime[name] || "");
    const sout = toMinutes(outTime[name] || "");
    const sStart = toMinutes(st);
    const sEnd = toMinutes(et);
    if (status[name] === "Absent" || status[name] === "On Leave") {
      setLateMin(prev => ({ ...prev, [name]: 0 }));
      setOtMin(prev => ({ ...prev, [name]: 0 }));
      return;
    }
    let late = 0, ot = 0;
    if (!isNaN(sin) && !isNaN(sStart)) late = Math.max(0, sin - sStart);
    if (!isNaN(sout) && !isNaN(sEnd)) ot = Math.max(0, sout - sEnd);
    setLateMin(prev => ({ ...prev, [name]: late }));
    setOtMin(prev => ({ ...prev, [name]: ot }));
    if (late > 0 && (!status[name] || status[name] === "")) {
      setStatus(prev => ({ ...prev, [name]: "Late" }));
    }
  }

  function parseCSV(text) {
    const rows = [];
    let i = 0, field = "", row = [], inQuotes = false;
    while (i < text.length) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else { inQuotes = false; }
        } else {
          field += ch;
        }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ',') { row.push(field); field = ""; }
        else if (ch === '\n' || ch === '\r') {
          if (field !== "" || row.length) { row.push(field); rows.push(row); row = []; field = ""; }
          if (ch === '\r' && text[i + 1] === '\n') i++;
        } else {
          field += ch;
        }
      }
      i++;
    }
    if (field !== "" || row.length) { row.push(field); rows.push(row); }
    return rows;
  }
  function getBioMap() {
    const map = {};
    const txt = mapText || "";
    const lines = txt.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    for (const line of lines) {
      const parts = line.split(",").map(s => s.trim());
      if (parts.length >= 2) map[parts[0]] = parts.slice(1).join(",").trim();
    }
    return map;
  }
  async function importCSVFile(file) {
    const text = await file.text();
    const rows = parseCSV(text).filter(r => r.length && r.some(c => String(c).trim() !== ""));
    if (rows.length < 2) { alert("No rows parsed"); return; }
    const headers = rows[0].map(h => String(h).trim().toLowerCase());
    const uidIdx = headers.findIndex(h => /user.?id|userid|id/i.test(h));
    const nameIdx = headers.findIndex(h => /name|employee/i.test(h));
    const timeIdx = headers.findIndex(h => /time|datetime|timestamp/i.test(h));
    if (timeIdx < 0) { alert("Missing timestamp column"); return; }
    const bioMap = getBioMap();
    const nameKey = (n) => String(n || "").trim();
    const empByName = new Map(employees.map(e => [nameKey(e.name), e]));
    const per = {};
    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      const uid = uidIdx >= 0 ? row[uidIdx] : "";
      const nm = nameIdx >= 0 ? row[nameIdx] : "";
      const when = row[timeIdx];
      if (!when) continue;
      let employeeName = nameKey(nm);
      if (!employeeName && uid && bioMap[uid]) employeeName = nameKey(bioMap[uid]);
      if (!employeeName) continue;
      const d = new Date(when);
      if (isNaN(d.getTime())) continue;
      const dStr = new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,10);
      if (dStr !== date) continue;
      const tStr = d.toTimeString().slice(0,5);
      const k = `${employeeName}|${dStr}`;
      if (!per[k]) per[k] = { name: employeeName, times: [] };
      per[k].times.push(tStr);
    }
    const entries = Object.values(per).map(p => {
      p.times.sort();
      return { name: p.name, in: p.times[0], out: p.times[p.times.length - 1] };
    });
    for (const ent of entries) {
      const e = empByName.get(ent.name) || {};
      const st = e.shiftStart || shiftStart;
      const et = e.shiftEnd || shiftEnd;
      const sStart = toMinutes(st);
      const sEnd = toMinutes(et);
      const sin = toMinutes(ent.in);
      const sout = toMinutes(ent.out);
      const late = (!isNaN(sin) && !isNaN(sStart)) ? Math.max(0, sin - sStart) : 0;
      const ot = (!isNaN(sout) && !isNaN(sEnd)) ? Math.max(0, sout - sEnd) : 0;
      const id = `${normalizeCampusLabel(campus)}|${date}|${ent.name.toLowerCase().replace(/\s+/g,"_")}`;
      const payload = {
        campus: normalizeCampusLabel(campus),
        date,
        employeeName: ent.name,
        status: late > 0 ? "Late" : "Present",
        inTime: ent.in || "",
        outTime: ent.out || "",
        minutesLate: late,
        overtimeMinutes: ot,
        notes: ""
      };
      try { await setDoc(doc(db, "employees_attendance", id), payload, { merge: true }); } catch(e) { void e; }
      setStatus(prev => ({ ...prev, [ent.name]: payload.status }));
      setInTime(prev => ({ ...prev, [ent.name]: payload.inTime }));
      setOutTime(prev => ({ ...prev, [ent.name]: payload.outTime }));
      setLateMin(prev => ({ ...prev, [ent.name]: payload.minutesLate }));
      setOtMin(prev => ({ ...prev, [ent.name]: payload.overtimeMinutes }));
    }
    setShowImport(false);
  }

  async function save() {
    setLoading(true);
    try {
      for (const e of employees) {
        const name = e.name || "";
        if (!name) continue;
        const payload = {
          campus,
          date,
          employeeName: name,
          status: status[name] || "Present",
          inTime: inTime[name] || "",
          outTime: outTime[name] || "",
          minutesLate: Number(lateMin[name] || 0),
          overtimeMinutes: Number(otMin[name] || 0),
          notes: note[name] || ""
        };
        await addDoc(collection(db, "employees_attendance"), payload);
      }
      alert("Attendance saved.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Daily Attendance</h3>
        <div className="d-flex align-items-center gap-2">
          <HRCampusSelector selectedCampus={campus} onChange={setCampus} allowAll={isExec} />
          <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted small">Shift</span>
            <input type="time" className="form-control" style={{width: 120}} value={shiftStart} onChange={e => setShiftStart(e.target.value)} />
            <span className="small">to</span>
            <input type="time" className="form-control" style={{width: 120}} value={shiftEnd} onChange={e => setShiftEnd(e.target.value)} />
          </div>
          <button className="btn btn-outline-primary" onClick={() => setShowImport(true)}>Import Biometric CSV</button>
          <button className="btn btn-outline-secondary" onClick={() => setShowMap(true)}>Map Device User IDs</button>
          <button className="btn btn-outline-secondary" onClick={() => { setCampus(campus); /* triggers effect */ }}>Reload</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>Save</button>
        </div>
      </div>
      {showImport && (
        <div className="card mb-3">
          <div className="card-header">Import Logs</div>
          <div className="card-body">
            <input type="file" accept=".csv,text/csv" onChange={e => { const f = e.target.files?.[0]; if (f) importCSVFile(f); }} />
            <div className="small text-muted mt-2">Columns accepted: UserID, Name, Timestamp</div>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <button className="btn btn-secondary" onClick={() => setShowImport(false)}>Close</button>
          </div>
        </div>
      )}
      {showMap && (
        <div className="card mb-3">
          <div className="card-header">UserID Mapping</div>
          <div className="card-body">
            <textarea className="form-control" rows={6} value={mapText} onChange={e => setMapText(e.target.value)} placeholder="UserID,Employee Name per line" />
          </div>
          <div className="card-footer d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={() => setShowMap(false)}>Close</button>
            <button className="btn btn-success" onClick={() => { try { localStorage.setItem("hr_att_bio_map_text", mapText || ""); } catch(e) { void e; } setShowMap(false); }}>Save</button>
          </div>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-sm table-hover">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>In</th>
              <th>Out</th>
              <th className="text-end">Late (min)</th>
              <th className="text-end">Overtime (min)</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(e => {
              const name = e.name || "";
              return (
                <tr key={e.id}>
                  <td>{name}</td>
                  <td>
                    <select className="form-select form-select-sm" value={status[name] || ""} onChange={ev => setStatus(prev => ({ ...prev, [name]: ev.target.value }))}>
                      <option value="">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="On Leave">On Leave</option>
                      <option value="Late">Late</option>
                    </select>
                  </td>
                  <td><input type="time" className="form-control form-control-sm" value={inTime[name] || ""} onChange={ev => { const v = ev.target.value; setInTime(prev => ({ ...prev, [name]: v })); setTimeout(() => computeFor(name, e), 0); }} /></td>
                  <td><input type="time" className="form-control form-control-sm" value={outTime[name] || ""} onChange={ev => { const v = ev.target.value; setOutTime(prev => ({ ...prev, [name]: v })); setTimeout(() => computeFor(name, e), 0); }} /></td>
                  <td><input type="number" className="form-control form-control-sm text-end" value={lateMin[name] || ""} onChange={ev => setLateMin(prev => ({ ...prev, [name]: ev.target.value }))} /></td>
                  <td><input type="number" className="form-control form-control-sm text-end" value={otMin[name] || ""} onChange={ev => setOtMin(prev => ({ ...prev, [name]: ev.target.value }))} /></td>
                  <td><input className="form-control form-control-sm" value={note[name] || ""} onChange={ev => setNote(prev => ({ ...prev, [name]: ev.target.value }))} /></td>
                </tr>
              );
            })}
            {employees.length === 0 && <tr><td colSpan="7" className="text-center text-muted">No employees found for campus</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
